/* eslint-disable */
// Dev-only mock data generator for externalCmdbRecords, matching the field
// shape returned by GetExternalCmdbRecords (see src/query/queries.js). Lets
// you reproduce the TEST-env scale (observed: ~300,159 records) locally,
// without hitting the real backend, to test table/pagination performance and
// to confirm the CMDB tab's lazy-fetch gate (Traceability.jsx) still fires
// exactly once per "open the tab" rather than on every page load.
//
// Enable with: localStorage.mockExternalCmdbRecords = "true"
// Optionally control the count: localStorage.mockExternalCmdbRecordsCount = "300000"
// Disable with: delete localStorage.mockExternalCmdbRecords

const RECORD_TYPES = ["service", "application", "server", "database"];
const CMDB_INSTANCES = ["rbspoc1.service-now.com", "rbspoc2.service-now.com"];

export function isMockExternalCmdbRecordsEnabled() {
  return typeof localStorage !== "undefined" && localStorage.mockExternalCmdbRecords === "true";
}

export function getMockExternalCmdbRecordsCount() {
  const n = parseInt(localStorage.mockExternalCmdbRecordsCount, 10);
  return Number.isFinite(n) && n > 0 ? n : 300000;
}

function mockRecord(i) {
  const recordType = RECORD_TYPES[i % RECORD_TYPES.length];
  const cmdbInstance = CMDB_INSTANCES[i % CMDB_INSTANCES.length];
  const hasUpstream = i % 5 === 0;
  const hasDownstream = i % 7 === 0;

  return {
    id: `mock-cmdb-${i}`,
    projectId: "mock-project",
    environmentId: "mock-environment",
    cmdbInstance,
    recordType,
    recordId: `sys-mock-${i.toString(16)}`,
    name: `Mock ${recordType} record #${i}`,
    upstreamRecordId: hasUpstream ? `mock-cmdb-${i - 1}` : null,
    downstreamRecordId: hasDownstream ? `mock-cmdb-${i + 1}` : null,
    ipAddress: i % 3 === 0 ? `10.${(i >> 16) & 255}.${(i >> 8) & 255}.${i & 255}` : null,
    url: i % 4 === 0 ? `https://${cmdbInstance}/nav_to.do?uri=cmdb_ci.do?sys_id=sys-mock-${i}` : null,
    lastModifiedDate: Date.now() - i * 1000,
    createdAt: Date.now() - i * 1000 * 2,
    updatedAt: Date.now() - i * 1000,
    __typename: "ExternalCmdbRecord",
  };
}

/** Generates the full mock array once (cached across calls in-session). */
let _cached = null;
export function generateMockExternalCmdbRecords(total = getMockExternalCmdbRecordsCount()) {
  if (_cached && _cached.length === total) return _cached;
  _cached = Array.from({ length: total }, (_, i) => mockRecord(i));
  return _cached;
}

/**
 * Mimics the paginated GetExternalCmdbRecords response shape (page/pageSize
 * in, { results, pageInfo: { totalPages, currentPage, totalItems } } out) so
 * ServerDataTable's fetchPage can be exercised locally before the backend's
 * server-side pagination lands. Slices the same cached array used by
 * generateMockExternalCmdbRecords, it just doesn't hand the whole thing back.
 */
export function getMockExternalCmdbRecordsPage({ page = 1, pageSize = 25, sort } = {}) {
  const total = getMockExternalCmdbRecordsCount();
  const all = generateMockExternalCmdbRecords(total);
  // Sort a copy — `all` is the shared, cached array. Unknown field: no-op
  // rather than replicating the backend's hard-error behavior; this is for
  // local UX testing, not contract verification.
  const field = sort?.field;
  const sorted = field && all[0]?.[field] !== undefined
    ? [...all].sort((a, b) => {
        const av = a[field], bv = b[field];
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sort.direction === "DESC" ? -cmp : cmp;
      })
    : all;
  const start = (page - 1) * pageSize;
  return {
    results: sorted.slice(start, start + pageSize),
    pageInfo: {
      totalPages: Math.max(1, Math.ceil(total / pageSize)),
      currentPage: page,
      totalItems: total,
    },
  };
}
